import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Users, Phone, Mail, Calendar, Search, ArrowUpDown, ArrowUp, ArrowDown, Edit2, X, Upload, FileText, CheckCircle, AlertCircle, MessageCircle, Send, GitMerge, Loader2 } from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

const FILTER_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'mtd', label: 'Month to Date' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_2_months', label: 'Last 2 Months' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Dates' },
];

const VENUES = ['MAKS Pickleball - ISF', 'Sports Complex', 'Bhayandar'];

const USER_TYPE_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'active', label: 'Active' },
  { value: 'repeat', label: 'Repeat' },
  { value: 'new', label: 'New' },
  { value: 'inactive', label: 'Inactive' },
];

// Helper function to format date as "1st Mar", "2nd Apr" etc
const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });
  
  const suffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  return `${day}${suffix(day)} ${month}`;
};

export default function ContactsPage() {
  const { admin } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filterType, setFilterType] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [venueFilter, setVenueFilter] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sourceOptions, setSourceOptions] = useState([]);
  const [typeCounts, setTypeCounts] = useState({ all: 0, active: 0, repeat: 0, new: 0, inactive: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [stats, setStats] = useState({ totalContacts: 0, totalBookings: 0, totalRevenue: 0 });
  const [sortBy, setSortBy] = useState('total_bookings');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editForm, setEditForm] = useState({ player_name: '', player_mobile: '', player_email: '' });
  const [saving, setSaving] = useState(false);
  
  // Import CSV modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Re-engagement broadcast modal state
  const [reengageOpen, setReengageOpen] = useState(false);
  const [reengageForm, setReengageForm] = useState({
    message_text: 'Hi {name}, we miss you at MAKS! Book your next game and get back on court. Reply to this message to grab a slot.',
    use_template: false,
    template_name: '',
    template_params: '',
    limit: 50,
    cooldown_days: 7,
  });
  const [reengagePreview, setReengagePreview] = useState(null);
  const [reengageSending, setReengageSending] = useState(false);
  const [reengageResult, setReengageResult] = useState(null);

  // Duplicate merge modal state
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [mergingGroup, setMergingGroup] = useState(null);  // currently merging
  const [mergeResult, setMergeResult] = useState(null);

  // Check if current user is admin
  const isAdmin = admin?.role === 'admin' || admin?.email === 'admin@maks.com';

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/contacts?filter_type=${filterType}&sort_by=${sortBy}&sort_order=${sortOrder}`;
      if (filterType === 'custom' && startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }
      if (venueFilter) {
        url += `&venue=${encodeURIComponent(venueFilter)}`;
      }
      if (userTypeFilter && userTypeFilter !== 'all') {
        url += `&user_type=${userTypeFilter}`;
      }
      if (sourceFilter && sourceFilter !== 'all') {
        url += `&source_platform=${encodeURIComponent(sourceFilter)}`;
      }
      
      const response = await axios.get(url);
      const data = response.data;
      
      setContacts(data.contacts || []);
      setDateRange(data.date_range || { start: '', end: '' });
      setSourceOptions(data.source_options || []);
      setTypeCounts(data.type_counts || { all: 0, active: 0, repeat: 0, new: 0, inactive: 0 });
      
      // Calculate stats
      const totalBookings = data.contacts.reduce((sum, c) => sum + c.total_bookings, 0);
      const totalRevenue = data.contacts.reduce((sum, c) => sum + c.total_amount, 0);
      setStats({
        totalContacts: data.total_contacts,
        totalBookings,
        totalRevenue
      });
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate, venueFilter, userTypeFilter, sourceFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredContacts(contacts);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = contacts.filter(
      (c) =>
        c.player_name.toLowerCase().includes(term) ||
        c.player_mobile.includes(searchTerm) ||
        (c.player_email && c.player_email.toLowerCase().includes(term))
    );
    setFilteredContacts(filtered);
  }, [contacts, searchTerm]);

  const handleExport = async () => {
    setExporting(true);
    try {
      let url = `${API}/contacts/export?filter_type=${filterType}&sort_by=${sortBy}&sort_order=${sortOrder}`;
      if (filterType === 'custom' && startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }
      if (venueFilter) {
        url += `&venue=${encodeURIComponent(venueFilter)}`;
      }
      if (userTypeFilter && userTypeFilter !== 'all') {
        url += `&user_type=${userTypeFilter}`;
      }
      if (sourceFilter && sourceFilter !== 'all') {
        url += `&source_platform=${encodeURIComponent(sourceFilter)}`;
      }

      const response = await axios.get(url, { responseType: 'blob' });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `maks_contacts_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Contacts exported successfully');
    } catch (error) {
      console.error('Failed to export contacts:', error);
      toast.error('Failed to export contacts');
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-slate-400" />;
    }
    return sortOrder === 'desc' 
      ? <ArrowDown className="w-4 h-4 ml-1 text-primary" />
      : <ArrowUp className="w-4 h-4 ml-1 text-primary" />;
  };

  const handleEditClick = (contact) => {
    setEditingContact(contact);
    setEditForm({
      player_name: contact.player_name || '',
      player_mobile: contact.player_mobile || '',
      player_email: contact.player_email || ''
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.player_name || !editForm.player_mobile) {
      toast.error('Name and Mobile are required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/contacts/${editingContact.player_mobile}`,
        editForm
      );
      toast.success(`Contact updated! ${response.data.bookings_updated} booking(s) modified.`);
      setEditModalOpen(false);
      setEditingContact(null);
      fetchContacts(); // Refresh the list
    } catch (error) {
      console.error('Failed to update contact:', error);
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(`${API}/contacts/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} contacts imported successfully!`);
        fetchContacts(); // Refresh the contacts list
      } else if (response.data.duplicates > 0) {
        toast.info(`All ${response.data.duplicates} contacts already exist`);
      } else {
        toast.warning('No contacts were imported');
      }
    } catch (error) {
      console.error('Failed to import contacts:', error);
      toast.error(error.response?.data?.detail || 'Failed to import contacts');
      setImportResult({
        error: true,
        message: error.response?.data?.detail || 'Failed to import contacts'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImportModal = () => {
    setImportModalOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  // ---- Re-engagement broadcast ----
  const buildReengagePayload = (dryRun) => ({
    filter_type: filterType,
    start_date: filterType === 'custom' ? startDate : undefined,
    end_date: filterType === 'custom' ? endDate : undefined,
    venue: venueFilter || undefined,
    user_type: userTypeFilter,
    source_platform: sourceFilter,
    message_text: reengageForm.message_text,
    use_template: reengageForm.use_template,
    template_name: reengageForm.template_name,
    template_params: reengageForm.template_params,
    limit: Number(reengageForm.limit) || 0,
    cooldown_days: Number(reengageForm.cooldown_days) || 0,
    dry_run: dryRun,
  });

  const openReengage = async () => {
    setReengageResult(null);
    setReengagePreview(null);
    setReengageOpen(true);
    try {
      const res = await axios.post(`${API}/contacts/reengage`, buildReengagePayload(true));
      setReengagePreview(res.data);
    } catch (error) {
      console.error('Failed to preview re-engagement:', error);
    }
  };

  // Re-fetch the preview whenever limit/cooldown change while the dialog is open
  const refreshReengagePreview = async (overrides = {}) => {
    try {
      const payload = { ...buildReengagePayload(true), ...overrides };
      const res = await axios.post(`${API}/contacts/reengage`, payload);
      setReengagePreview(res.data);
    } catch (error) {
      console.error('Failed to refresh preview:', error);
    }
  };

  const handleSendReengage = async () => {
    if (!reengageForm.use_template && !reengageForm.message_text.trim()) {
      toast.error('Enter a message or switch on "Use approved template"');
      return;
    }
    if (reengageForm.use_template && !reengageForm.template_name.trim()) {
      toast.error('Template name is required');
      return;
    }
    setReengageSending(true);
    setReengageResult(null);
    try {
      const res = await axios.post(`${API}/contacts/reengage`, buildReengagePayload(false));
      setReengageResult(res.data);
      if (res.data.sent > 0) {
        toast.success(`Re-engagement sent to ${res.data.sent} contact(s)`);
      } else if (res.data.eligible === 0) {
        toast.info('No eligible contacts (all within cooldown or none matched)');
      } else {
        toast.warning('No messages were sent — check WhatsApp setup');
      }
    } catch (error) {
      console.error('Failed to send re-engagement:', error);
      toast.error(error.response?.data?.detail || 'Failed to send re-engagement');
    } finally {
      setReengageSending(false);
    }
  };

  const getFilterLabel = () => {
    const option = FILTER_OPTIONS.find(o => o.value === filterType);
    return option ? option.label : 'This Year';
  };

  const openMergeDuplicates = async () => {
    setMergeOpen(true);
    setMergeResult(null);
    setMergeLoading(true);
    try {
      const res = await axios.get(`${API}/contacts/find-duplicates`);
      setDuplicateGroups(res.data.groups || []);
    } catch (err) {
      toast.error('Failed to detect duplicates');
    } finally {
      setMergeLoading(false);
    }
  };

  const handleMergeGroup = async (group) => {
    setMergingGroup(group.normalized_mobile);
    try {
      const res = await axios.post(`${API}/contacts/merge`, {
        primary_mobile: group.suggested_primary,
        secondary_mobile: group.entries.find(e => e.raw_mobile !== group.suggested_primary)?.raw_mobile,
        keep_name: group.suggested_name,
      });
      setMergeResult({ ...res.data, normalized: group.normalized_mobile });
      setDuplicateGroups(prev => prev.filter(g => g.normalized_mobile !== group.normalized_mobile));
      toast.success(`Merged: ${res.data.bookings_updated} booking(s) updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    } finally {
      setMergingGroup(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="contacts-page">
        {/* Header */}
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
            Contacts
          </h1>
          <p className="text-slate-600 mt-1">
            {filteredContacts.length} player{filteredContacts.length !== 1 ? 's' : ''} found • {getFilterLabel()}
            {venueFilter && ` • ${venueFilter}`}
          </p>
        </div>
        <div className="flex justify-center gap-3 flex-wrap">
          {isAdmin && (
            <Button
              onClick={() => setImportModalOpen(true)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
          )}
          <Button
            onClick={handleExport}
            disabled={exporting || filteredContacts.length === 0}
            variant="outline"
            data-testid="contacts-export-excel-btn"
          >
            <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
          {isAdmin && (
            <Button
              onClick={openReengage}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="reengage-open-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Send Re-engagement
            </Button>
          )}
          {isAdmin && (
            <Button
              onClick={openMergeDuplicates}
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              data-testid="merge-duplicates-btn"
            >
              <GitMerge className="w-4 h-4 mr-2" />
              Merge Duplicates
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Players</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalContacts}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Bookings</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalBookings}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 border-slate-200">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name, mobile or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="contacts-search"
              />
            </div>

            {/* Filter Type */}
            <div className="w-full lg:w-40">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type-select">
                  <SelectValue placeholder="Select Period" />
                </SelectTrigger>
                <SelectContent>
                  {FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Venue Filter */}
            <div className="w-full lg:w-44">
              <Select value={venueFilter || "all"} onValueChange={(value) => setVenueFilter(value === "all" ? "" : value)}>
                <SelectTrigger data-testid="filter-venue-select">
                  <SelectValue placeholder="All Venues" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Venues</SelectItem>
                  {VENUES.map((venue) => (
                    <SelectItem key={venue} value={venue}>
                      {venue}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* User Type Filter */}
            <div className="w-full lg:w-40">
              <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
                <SelectTrigger data-testid="filter-user-type-select">
                  <SelectValue placeholder="User Type" />
                </SelectTrigger>
                <SelectContent>
                  {USER_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                      {typeCounts[option.value] !== undefined && (
                        <span className="text-slate-400"> ({typeCounts[option.value]})</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Platform Filter */}
            <div className="w-full lg:w-44">
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger data-testid="filter-source-select">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {sourceOptions.map((src) => (
                    <SelectItem key={src} value={src}>
                      {src}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range */}
            {filterType === 'custom' && (
              <>
                <div className="w-full lg:w-36">
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder="Start Date"
                    data-testid="start-date-input"
                  />
                </div>
                <div className="w-full lg:w-36">
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder="End Date"
                    data-testid="end-date-input"
                  />
                </div>
              </>
            )}
          </div>
          
          {dateRange.start && dateRange.end && (
            <p className="text-xs text-slate-500 mt-2">
              Showing data from {formatShortDate(dateRange.start)} to {formatShortDate(dateRange.end)}
            </p>
          )}
        </Card>

        {/* Segment Chips - one-tap filter */}
        <div className="flex flex-wrap gap-2" data-testid="segment-chips">
          {USER_TYPE_OPTIONS.map((opt) => {
            const active = userTypeFilter === opt.value;
            const count = typeCounts[opt.value] ?? 0;
            const colorMap = {
              all: active ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400',
              active: active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-200 hover:border-emerald-400',
              repeat: active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-700 border-indigo-200 hover:border-indigo-400',
              new: active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:border-blue-400',
              inactive: active ? 'bg-slate-600 text-white border-slate-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400',
            };
            return (
              <button
                key={opt.value}
                onClick={() => setUserTypeFilter(opt.value)}
                className={`px-3.5 py-1.5 rounded-full border text-sm font-medium transition-colors ${colorMap[opt.value]}`}
                data-testid={`segment-chip-${opt.value}`}
              >
                {opt.label}
                <span className={`ml-1.5 text-xs ${active ? 'opacity-90' : 'opacity-60'}`}>({count})</span>
              </button>
            );
          })}
        </div>

        {/* Contacts Table */}
        {filteredContacts.length === 0 ? (
          <Card className="p-12 text-center border-slate-200">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No contacts found for this period</p>
          </Card>
        ) : (
          <div className="overflow-x-auto">
            <Card className="border-slate-200">
              <table className="w-full text-sm" data-testid="contacts-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 font-semibold text-slate-700">#</th>
                    <th className="text-left p-4 font-semibold text-slate-700">Player Name</th>
                    <th className="text-left p-4 font-semibold text-slate-700">Mobile Number</th>
                    <th className="text-left p-4 font-semibold text-slate-700">Email ID</th>
                    <th 
                      className="text-left p-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort('total_bookings')}
                    >
                      <div className="flex items-center">
                        Total Bookings
                        {getSortIcon('total_bookings')}
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort('total_amount')}
                    >
                      <div className="flex items-center">
                        Total Amount
                        {getSortIcon('total_amount')}
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort('last_booking_date')}
                    >
                      <div className="flex items-center">
                        Last Booking
                        {getSortIcon('last_booking_date')}
                      </div>
                    </th>
                    <th className="text-left p-4 font-semibold text-slate-700">Games</th>
                    {isAdmin && (
                      <th className="text-left p-4 font-semibold text-slate-700">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map((contact, index) => (
                    <tr
                      key={`${contact.player_name}-${contact.player_mobile}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`contact-row-${index}`}
                    >
                      <td className="p-4 text-slate-500">{index + 1}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                            {contact.player_name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <span className="font-medium text-slate-900">{contact.player_name}</span>
                          {contact.user_type && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${
                                contact.user_type === 'active'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : contact.user_type === 'new'
                                  ? 'bg-blue-100 text-blue-700'
                                  : contact.user_type === 'inactive'
                                  ? 'bg-slate-200 text-slate-600'
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                              data-testid={`contact-user-type-${index}`}
                            >
                              {contact.is_repeat ? 'Repeat' : contact.user_type}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-4 h-4 text-slate-400" />
                          {contact.player_mobile}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {contact.player_email ? (
                            <span className="text-slate-600">{contact.player_email}</span>
                          ) : (
                            <span className="text-slate-400 italic">-</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          {contact.total_bookings} booking{contact.total_bookings !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="p-4 font-semibold text-slate-900">
                        ₹{contact.total_amount?.toLocaleString() || 0}
                      </td>
                      <td className="p-4 text-slate-600">
                        {formatShortDate(contact.last_booking_date)}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {contact.games_played?.slice(0, 2).map((game) => (
                            <span
                              key={game}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                            >
                              {game}
                            </span>
                          ))}
                          {contact.games_played?.length > 2 && (
                            <span className="text-xs text-slate-500">
                              +{contact.games_played.length - 2} more
                            </span>
                          )}
                        </div>
                      </td>
                      {isAdmin && (
                        <td className="p-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(contact)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            data-testid={`edit-contact-${index}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        )}
      </div>

      {/* Edit Contact Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Contact
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Player Name *</Label>
              <Input
                id="edit-name"
                value={editForm.player_name}
                onChange={(e) => setEditForm({...editForm, player_name: e.target.value})}
                placeholder="Enter player name"
                data-testid="edit-contact-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-mobile">Mobile Number *</Label>
              <Input
                id="edit-mobile"
                value={editForm.player_mobile}
                onChange={(e) => setEditForm({...editForm, player_mobile: e.target.value})}
                placeholder="Enter mobile number"
                data-testid="edit-contact-mobile"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email (Optional)</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.player_email}
                onChange={(e) => setEditForm({...editForm, player_email: e.target.value})}
                placeholder="Enter email address"
                data-testid="edit-contact-email"
              />
            </div>

            {editingContact && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                <p><strong>Note:</strong> This will update the contact details across all {editingContact.total_bookings} booking(s) for this player.</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Modal */}
      <Dialog open={importModalOpen} onOpenChange={handleCloseImportModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              Import Contacts from CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Instructions */}
            <div className="bg-blue-50 rounded-lg p-4 text-sm">
              <div className="flex items-start gap-2">
                <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-blue-900">CSV Format Requirements</p>
                  <p className="text-blue-700 mt-1">Your CSV file should have these columns:</p>
                  <ul className="list-disc list-inside text-blue-700 mt-1 space-y-0.5">
                    <li><strong>name</strong> (required) - Player's full name</li>
                    <li><strong>mobile</strong> (required) - 10-digit mobile number</li>
                    <li><strong>email</strong> (optional) - Email address</li>
                  </ul>
                  <button
                    onClick={() => {
                      const csvContent = "name,mobile,email\nRahul Sharma,9876543210,rahul@example.com\nPriya Patel,8765432109,priya@example.com";
                      const blob = new Blob([csvContent], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = 'sample_contacts.csv';
                      link.click();
                      window.URL.revokeObjectURL(url);
                    }}
                    className="mt-2 text-blue-600 hover:text-blue-800 font-semibold underline text-sm flex items-center gap-1"
                    data-testid="download-sample-csv"
                  >
                    <Download className="w-4 h-4" />
                    Download Sample CSV
                  </button>
                </div>
              </div>
            </div>
            
            {/* File Input */}
            <div className="space-y-2">
              <Label htmlFor="csv-file">Select CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={(e) => {
                  setImportFile(e.target.files?.[0] || null);
                  setImportResult(null);
                }}
                className="cursor-pointer"
                data-testid="csv-file-input"
              />
              {importFile && (
                <p className="text-sm text-slate-600">
                  Selected: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            
            {/* Import Result */}
            {importResult && !importResult.error && (
              <div className="bg-green-50 rounded-lg p-4 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-900">Import Complete</p>
                    <ul className="text-green-700 mt-1 space-y-0.5">
                      <li><strong>{importResult.imported}</strong> contacts imported</li>
                      {importResult.duplicates > 0 && (
                        <li><strong>{importResult.duplicates}</strong> duplicates skipped</li>
                      )}
                      {importResult.skipped > 0 && (
                        <li><strong>{importResult.skipped}</strong> rows skipped (invalid data)</li>
                      )}
                    </ul>
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-amber-700 font-medium">Errors:</p>
                        <ul className="text-amber-600 text-xs mt-1">
                          {importResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                          {importResult.errors.length > 5 && (
                            <li>...and {importResult.errors.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {importResult?.error && (
              <div className="bg-red-50 rounded-lg p-4 text-sm">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-900">Import Failed</p>
                    <p className="text-red-700 mt-1">{importResult.message}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCloseImportModal} disabled={importing}>
              {importResult ? 'Close' : 'Cancel'}
            </Button>
            {!importResult && (
              <Button 
                onClick={handleImportCSV} 
                disabled={importing || !importFile} 
                className="bg-green-600 hover:bg-green-700"
                data-testid="import-csv-submit"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Contacts
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-engagement Broadcast Modal */}
      <Dialog open={reengageOpen} onOpenChange={(o) => { setReengageOpen(o); if (!o) setReengageResult(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-600" />
              Send WhatsApp Re-engagement
            </DialogTitle>
          </DialogHeader>

          {reengageResult ? (
            <div className="space-y-4 py-4" data-testid="reengage-result">
              <div className="bg-emerald-50 rounded-lg p-4 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-emerald-900">Campaign Complete</p>
                    <ul className="text-emerald-700 mt-1 space-y-0.5">
                      <li><strong>{reengageResult.sent}</strong> message(s) sent</li>
                      {reengageResult.failed > 0 && <li><strong>{reengageResult.failed}</strong> failed</li>}
                      <li><strong>{reengageResult.skipped_cooldown}</strong> skipped (within {reengageForm.cooldown_days}-day cooldown)</li>
                      <li><strong>{reengageResult.matched}</strong> total in segment</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Segment summary */}
              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700" data-testid="reengage-segment-summary">
                <p>
                  Targeting segment:{' '}
                  <strong className="capitalize">{userTypeFilter === 'all' ? 'All users' : userTypeFilter}</strong>
                  {sourceFilter !== 'all' && <> · source <strong>{sourceFilter}</strong></>}
                  {venueFilter && <> · <strong>{venueFilter}</strong></>}
                </p>
                {reengagePreview && (
                  <p className="mt-1 text-slate-600">
                    <strong>{reengagePreview.eligible}</strong> will receive ·{' '}
                    <strong>{reengagePreview.skipped_cooldown}</strong> skipped (cooldown) ·{' '}
                    {reengagePreview.matched} matched
                  </p>
                )}
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="reengage-msg">Message {'{name}'} is replaced with the contact's name</Label>
                <Textarea
                  id="reengage-msg"
                  rows={4}
                  value={reengageForm.message_text}
                  onChange={(e) => setReengageForm({ ...reengageForm, message_text: e.target.value })}
                  disabled={reengageForm.use_template}
                  placeholder="Hi {name}, we miss you at MAKS! ..."
                  data-testid="reengage-message"
                />
              </div>

              {/* Use template toggle */}
              <div className="flex items-center justify-between bg-amber-50 rounded-lg p-3">
                <div className="pr-3">
                  <p className="text-sm font-medium text-amber-900">Use approved template</p>
                  <p className="text-xs text-amber-700">Required to reach contacts outside the 24-hour window (e.g. inactive users).</p>
                </div>
                <Switch
                  checked={reengageForm.use_template}
                  onCheckedChange={(v) => setReengageForm({ ...reengageForm, use_template: v })}
                  data-testid="reengage-use-template"
                />
              </div>

              {reengageForm.use_template && (
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="reengage-tpl">Template Name</Label>
                    <Input
                      id="reengage-tpl"
                      value={reengageForm.template_name}
                      onChange={(e) => setReengageForm({ ...reengageForm, template_name: e.target.value })}
                      placeholder="maks_reengagement"
                      data-testid="reengage-template-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reengage-params">Template Params (comma-separated)</Label>
                    <Input
                      id="reengage-params"
                      value={reengageForm.template_params}
                      onChange={(e) => setReengageForm({ ...reengageForm, template_params: e.target.value })}
                      placeholder="e.g. MAKS,20%25"
                      data-testid="reengage-template-params"
                    />
                  </div>
                </div>
              )}

              {/* Limit + cooldown */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="reengage-limit">Max contacts to send</Label>
                  <Input
                    id="reengage-limit"
                    type="number"
                    min={1}
                    value={reengageForm.limit}
                    onChange={(e) => {
                      const v = e.target.value;
                      setReengageForm({ ...reengageForm, limit: v });
                      refreshReengagePreview({ limit: Number(v) || 0 });
                    }}
                    data-testid="reengage-limit"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reengage-cooldown">Cooldown (days)</Label>
                  <Input
                    id="reengage-cooldown"
                    type="number"
                    min={0}
                    value={reengageForm.cooldown_days}
                    onChange={(e) => {
                      const v = e.target.value;
                      setReengageForm({ ...reengageForm, cooldown_days: v });
                      refreshReengagePreview({ cooldown_days: Number(v) || 0 });
                    }}
                    data-testid="reengage-cooldown"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Contacts already messaged within the cooldown window are automatically skipped.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReengageOpen(false)} disabled={reengageSending}>
              {reengageResult ? 'Close' : 'Cancel'}
            </Button>
            {!reengageResult && (
              <Button
                onClick={handleSendReengage}
                disabled={reengageSending || (reengagePreview && reengagePreview.eligible === 0)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="reengage-send-btn"
              >
                {reengageSending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send to {reengagePreview ? reengagePreview.eligible : ''} contact{reengagePreview && reengagePreview.eligible === 1 ? '' : 's'}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Duplicates Dialog */}
      <Dialog open={mergeOpen} onOpenChange={(o) => { setMergeOpen(o); if (!o) { setDuplicateGroups([]); setMergeResult(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="w-5 h-5 text-orange-600" />
              Merge Duplicate Contacts
            </DialogTitle>
          </DialogHeader>

          {mergeLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              <span className="ml-3 text-slate-600">Scanning for duplicates…</span>
            </div>
          ) : duplicateGroups.length === 0 ? (
            <div className="py-10 text-center text-slate-500" data-testid="no-duplicates-msg">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <p className="font-medium text-slate-700">
                {mergeResult ? `All done! ${mergeResult.bookings_updated} booking(s) merged.` : 'No duplicates found.'}
              </p>
              <p className="text-sm mt-1">All contacts have unique mobile numbers.</p>
            </div>
          ) : (
            <div className="space-y-3 py-4" data-testid="duplicate-groups-list">
              <p className="text-sm text-slate-600 bg-orange-50 rounded-lg px-3 py-2 border border-orange-200">
                <strong>{duplicateGroups.length}</strong> duplicate group(s) found — same 10-digit mobile stored in different formats.
                Merging moves all bookings to the primary number and never overwrites a real name with a generic one.
              </p>
              {duplicateGroups.map((group) => (
                <div key={group.normalized_mobile} className="border border-slate-200 rounded-lg p-4 space-y-2" data-testid={`dup-group-${group.normalized_mobile}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{group.normalized_mobile}</p>
                      <p className="text-xs text-slate-500">{group.total_bookings} total booking(s) across {group.entries.length} variants</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleMergeGroup(group)}
                      disabled={mergingGroup === group.normalized_mobile}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid={`merge-btn-${group.normalized_mobile}`}
                    >
                      {mergingGroup === group.normalized_mobile
                        ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Merging…</>
                        : <><GitMerge className="w-3 h-3 mr-1" />Merge</>
                      }
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {group.entries.map((entry, i) => (
                      <div key={entry.raw_mobile} className={`flex items-center gap-2 text-sm px-2 py-1 rounded ${i === 0 ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${i === 0 ? 'bg-green-200 text-green-800' : 'bg-slate-200 text-slate-700'}`}>
                          {i === 0 ? 'KEEP' : 'MERGE'}
                        </span>
                        <span className="font-mono text-slate-700">{entry.raw_mobile}</span>
                        <span className="text-slate-500">·</span>
                        <span className="text-slate-600">{entry.names.join(', ') || 'no name'}</span>
                        <span className="ml-auto text-slate-400">{entry.booking_count} booking(s)</span>
                      </div>
                    ))}
                    {group.suggested_name && (
                      <p className="text-xs text-slate-500 pl-1">Name after merge: <strong>{group.suggested_name}</strong></p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
